import { globalEventBus } from '@fiverr-private/futile';
import { logger } from '@fiverr-private/obs';
import { POLLING_ATTEMPTS, POLLING_DELAY, EVENTS, EVENT_READY_SYMBOLS } from '../constants';
import { sendInitialChatData } from '../../api/sendInitialChatData';
import type { OpenFloatingChatEventProps, OpenFloatingChatTooltipEventProps } from '../../types';

const waitForListenerReady = (readySymbol: keyof typeof EVENT_READY_SYMBOLS) =>
    new Promise<void>((resolve, reject) => {
        const mappedSymbol = EVENT_READY_SYMBOLS[readySymbol];
        const isReady = () => typeof window !== 'undefined' && mappedSymbol && window[mappedSymbol];

        if (isReady()) {
            resolve();
            return;
        }

        let currentAttempt = 0;

        const interval = setInterval(() => {
            if (currentAttempt >= POLLING_ATTEMPTS) {
                clearInterval(interval);
                reject();
                return;
            }

            if (isReady()) {
                clearInterval(interval);
                resolve();
            }

            ++currentAttempt;
        }, POLLING_DELAY);
    });

/**
 * Emits an event to open the floating chat.
 * Waits until the floating chat listener is ready before emitting.
 * Also triggers the chat initialization API call if required data is provided.
 */
export const openFloatingChat = async <T>(payload?: OpenFloatingChatEventProps<T>) => {
    try {
        await waitForListenerReady('GO_OPEN_FLOATING_CHAT');
        await globalEventBus.deferred(EVENTS.GO_OPEN_FLOATING_CHAT, { data: payload?.data });

        if (payload?.data) {
            // Fire and forget - service handles its own error logging
            sendInitialChatData(payload.data);
        }
    } catch (error) {
        logger.error('[Go Floating Chat Triggers] Failed to open floating chat', {
            error: error instanceof Error ? error.message : 'Unknown error',
        });
    }
};

/**
 * Emits an event to open the floating chat tooltip.
 * Waits until the floating chat listener is ready before emitting.
 */
export const openFloatingChatTooltip = async <T>(payload?: OpenFloatingChatTooltipEventProps<T>) => {
    try {
        await waitForListenerReady('GO_OPEN_FLOATING_CHAT_TOOLTIP');
        await globalEventBus.deferred(EVENTS.GO_OPEN_FLOATING_CHAT_TOOLTIP, { data: payload?.data });
    } catch (error) {
        logger.error('[Go Floating Chat Triggers] Failed to open floating chat tooltip', {
            error: error instanceof Error ? error.message : 'Unknown error',
        });
    }
};
